// Demo account utilities
import { log } from '@/lib/logger';

// The email address used by the shared demo account (demo@nynch.com).
// This is a real Supabase user. Visitors log in with real credentials and see
// seeded data that can be restored with the demo reset flow.
export const DEMO_USER_EMAIL = 'demo@nynch.com';

/**
 * Check if a Supabase User is the shared demo account.
 *
 * Use this to adjust behavior such as notifications, onboarding, AI assistant,
 * and high-risk mutations for the demo user.
 *
 * Works with the User object from useAuth() or supabase.auth.getUser().
 */
export function isDemoUser(user: { email?: string } | null | undefined): boolean {
  return user?.email === DEMO_USER_EMAIL;
}

/**
 * Read and parse a Supabase auth-token entry from localStorage. Returns the
 * parsed payload, or null when the value is missing/unparseable. Centralised
 * so callers don't replicate the JSON-parse-with-fallback shape.
 */
function readParsedSupabaseToken(key: string): Record<string, unknown> | null {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/** Extract the user's email from a parsed Supabase auth-token payload. */
function emailFromTokenPayload(parsed: Record<string, unknown> | null): string | null {
  if (!parsed) return null;
  const userObj = (parsed.user ?? null) as { email?: unknown } | null;
  if (typeof userObj?.email === 'string') return userObj.email;
  const sessionObj = (parsed.currentSession ?? null) as { user?: { email?: unknown } } | null;
  const sessionEmail = sessionObj?.user?.email;
  if (typeof sessionEmail === 'string') return sessionEmail;
  return null;
}

/**
 * Check if the app is currently in demo mode.
 *
 * Synchronous so it works in render paths without an auth-hook subscription.
 * Two signals, in order of preference:
 *   1. `demo_authenticated` sessionStorage flag — set by the public demo
 *      landing page when a visitor clicks "Try the demo".
 *   2. Inspect Supabase auth tokens in localStorage and only return true when
 *      a parsed payload's email is exactly the demo email. Substring fallback
 *      was removed (PR fixing the persistMessage demo-gate bug) because a
 *      stale token for a different user with the demo email merely appearing
 *      anywhere in the payload was enough to false-positive the entire app
 *      into demo mode.
 */
export function isDemoMode(): boolean {
  try {
    if (typeof sessionStorage !== 'undefined') {
      if (sessionStorage.getItem('demo_authenticated') === 'true') return true;
    }
    if (typeof localStorage === 'undefined') return false;
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || (!key.startsWith('sb-') && !key.includes('supabase.auth'))) continue;
      const parsed = readParsedSupabaseToken(key);
      if (emailFromTokenPayload(parsed) === DEMO_USER_EMAIL) return true;
    }
    return false;
  } catch {
    return false;
  }
}

// Check if there's a real Supabase auth session in localStorage
// Used by cleanupDemoStateIfNeeded to distinguish anonymous visitors
// from real users whose session hasn't loaded yet
export function hasRealAuthSession(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && (key.startsWith('sb-') || key.includes('supabase.auth'))) {
        const value = localStorage.getItem(key);
        if (value) {
          try {
            const parsed = JSON.parse(value);
            if (parsed?.user?.id || parsed?.access_token || parsed?.currentSession?.user) {
              return true;
            }
          } catch {
            // Not JSON, skip
          }
        }
      }
    }
    return false;
  } catch {
    return false;
  }
}

/**
 * The fixed storage key used by the /demo-preview iframe's supabase client
 * (see `src/integrations/supabase/client.ts`). When a real user is detected
 * outside that route we can safely clear this token if it still holds a
 * demo session — it would otherwise persist and make `isDemoMode()` true
 * for the real user's session.
 */
const DEMO_PREVIEW_STORAGE_KEY = 'sb-demo-preview-auth';

/**
 * Clean up lingering demo state when a real user is detected.
 * Call this explicitly when auth state settles (e.g., in a useEffect after user is confirmed).
 *
 * Clears the sessionStorage demo flag AND, when the active session is not the
 * demo user, the /demo-preview iframe's stale auth token. We deliberately do
 * NOT iterate all supabase tokens in localStorage — the active session's
 * token lives there too and must not be touched.
 *
 * @param activeUserEmail — the currently-authenticated user's email. When
 *        omitted, only the sessionStorage flag is cleared (safe default).
 * @returns true if any demo state was cleared, false if no cleanup was needed
 */
export function cleanupDemoStateIfNeeded(activeUserEmail?: string | null): boolean {
  if (!hasRealAuthSession()) return false;

  let cleared = false;

  if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('demo_authenticated') === 'true') {
    clearDemoAuth();
    cleared = true;
  }

  if (typeof localStorage !== 'undefined' && activeUserEmail && activeUserEmail !== DEMO_USER_EMAIL) {
    const parsed = readParsedSupabaseToken(DEMO_PREVIEW_STORAGE_KEY);
    if (emailFromTokenPayload(parsed) === DEMO_USER_EMAIL) {
      localStorage.removeItem(DEMO_PREVIEW_STORAGE_KEY);
      cleared = true;
    }
  }

  if (cleared) {
    log.info('Cleared lingering demo state - real auth session detected');
  }
  return cleared;
}

// Clear demo authentication and modifications
export function clearDemoAuth() {
  sessionStorage.removeItem('demo_authenticated');
  sessionStorage.removeItem('demo_modifications');
  sessionStorage.removeItem('demo_user');
}

// Extract name from email
export function extractNameFromEmail(email: string): string {
  const localPart = email.split('@')[0] ?? '';
  const name = localPart
    .replace(/[._]/g, ' ')
    .replace(/\d+/g, '')
    .split(' ')
    .filter(Boolean)
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
  return name || 'Demo User';
}
